import type { PlayerColor } from '@/shared/board/types'
import type { Combo, Move } from '@/shared/game/types'
import { movesForDie } from './moves'
import { applyMove, type GameState } from './state'

/**
 * Какие ходы игроку РАЗРЕШЕНО сделать сейчас — порт
 * App\Domain\Ludo\TurnResolver.
 *
 * Отличается от генератора тем, что учитывает правило максимума:
 *
 *   1. Есть ход, после которого играется и второй кубик, — обязан
 *      использовать оба, в любом порядке и любыми фишками.
 *   2. Оба не играются никак — играется больший кубик, а если и им нельзя,
 *      то меньший.
 *   3. Не играется ни один — ходов нет, кубики сгорают.
 *
 * Прыжки под это правило не подводятся: они не обязательны.
 */
export function allowedMoves(state: GameState, player: PlayerColor, dice: number[]): Move[] {
  const jumps: Move[] = []
  const plain: Move[] = []

  for (const die of [...new Set(dice)]) {
    for (const move of movesForDie(state, player, die)) {
      if (move.kind === 'jump') {
        jumps.push(move)
      } else {
        plain.push(move)
      }
    }
  }

  return [...maximal(state, player, dice, plain), ...jumps]
}

/**
 * Ходы одной фишкой на оба кубика сразу — «сумма».
 *
 * Отдельным видом хода сумма не заводится: через фишки нельзя
 * перепрыгивать, поэтому ход на 5+1 проходит те же клетки, что и два хода
 * подряд той же фишкой. Но игроку это не помогает — пары собираются здесь.
 */
export function combos(state: GameState, player: PlayerColor, dice: number[]): Move[][] {
  if (dice.length < 2) return []

  const pairs: Move[][] = []
  const seen = new Set<string>()

  for (const first of allowedMoves(state, player, dice)) {
    const rest = without(dice, first.die)
    const after = applyMove(state, first)

    for (const second of allowedMoves(after, player, rest)) {
      if (second.token !== first.token) continue

      const key = [
        first.token,
        second.to.location,
        second.to.index,
        captureKey(first),
        captureKey(second),
      ].join(':')

      if (seen.has(key)) continue

      seen.add(key)
      pairs.push([first, second])
    }
  }

  return pairs
}

/** Пара ходов в том виде, в каком её ждёт экран партии. */
export function comboView(pair: Move[]): Combo {
  const last = pair[pair.length - 1]

  return {
    steps: pair.map((move) => ({ token: move.token, die: move.die, kind: move.kind })),
    token: last.token,
    dice: pair.map((move) => move.die),
    to: last.to,
    path: pair.flatMap((move) => move.path),
    captures: pair
      .map((move) => move.captures)
      .filter((capture): capture is NonNullable<Move['captures']> => capture !== null),
  }
}

function captureKey(move: Move): string {
  return move.captures === null ? '-' : `${move.captures.color}#${move.captures.token}`
}

/** Отбор по правилу максимума среди обычных ходов. */
function maximal(state: GameState, player: PlayerColor, dice: number[], moves: Move[]): Move[] {
  if (dice.length < 2 || moves.length === 0) return moves

  // Ходы, после которых остаётся сыграть и второй кубик.
  const both = moves.filter((move) =>
    playable(applyMove(state, move), player, without(dice, move.die)),
  )

  if (both.length > 0) return both

  // Оба не выходят: играется больший кубик, а если он не идёт — меньший.
  for (const die of [...dice].sort((one, other) => other - one)) {
    const onlyThis = moves.filter((move) => move.die === die)

    if (onlyThis.length > 0) return onlyThis
  }

  return []
}

/**
 * Найдётся ли хоть один обычный ход на эти кубики.
 *
 * Прыжки не в счёт: они не обязательны, и наличие прыжка не должно
 * заставлять играть кубик, которым иначе ходить нечем.
 */
function playable(state: GameState, player: PlayerColor, dice: number[]): boolean {
  for (const die of [...new Set(dice)]) {
    if (movesForDie(state, player, die).some((move) => move.kind !== 'jump')) return true
  }

  return false
}

/** Убрать один кубик указанного значения. */
export function without(dice: number[], die: number): number[] {
  const rest = [...dice]
  const at = rest.indexOf(die)

  if (at !== -1) rest.splice(at, 1)

  return rest
}
